import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  DollarSign,
  TrendingUp,
  Handshake,
  Trophy,
  Crown,
  Medal,
  Award,
  Flame,
  Star,
  AlertCircle,
  Building2,
  Users,
  Loader2,
  Plus,
  X,
  Calendar,
  Zap,
  UserCircle,
  ChevronDown,
  Check,
  Lock,
  Swords,
  Gift,
  RefreshCcw,
} from "lucide-react"
import { StreakPopup, type StreakData, type StreakDay } from "@/components/dashboard/StreakPopup"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import { useAuth, type LeaderboardEntry, type LeaderboardData, type Deal, type AgentInfo, type DealSlotReward } from "@/contexts/AuthContext"
import { DealRewardSlotStep } from "@/components/dashboard/DealRewardSlotStep"
import coinImg from "@/public/coin.png"
import goldCoinLottie from "@/public/lotties/gold-coin.lottie"
import silverCoinLottie from "@/public/lotties/silver-coin.lottie"
import bronzeCoinLottie from "@/public/lotties/bronze-coin.lottie"
import diamondLottie from "@/public/lotties/diamond.lottie"
import platinumLottie from "@/public/lotties/platinum.lottie"
import moneyImg from "@/public/money.png"
import bannerImg from "@/public/banners/banner1.png"
import battlePassHeaderBg from "@/public/banners/battle-pass-header-bg.png"
import battlePassPillCutout from "@/public/banners/battle-pass-pill-cutout.png"
import leaderboardHeaderBg from "@/public/banners/leaderboard-header-bg.png"
import recentDealsHeaderBg from "@/public/banners/recent-deals-header-bg.png"
import earningsCardBg from "@/public/dashboard-cards/earnings.png"
import dealsCardBg from "@/public/dashboard-cards/deals.png"
import bonusCardBg from "@/public/dashboard-cards/bonus.png"

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring" as const, bounce: 0.4 },
  },
}

function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  color,
  backgroundImage,
  backgroundPosition = "center",
  delay = 0,
  onPlusClick,
}: {
  icon: React.ElementType
  label: string
  value: string
  subValue?: string
  color: string
  backgroundImage?: string
  backgroundPosition?: string
  delay?: number
  onPlusClick?: () => void
}) {
  const hasArt = Boolean(backgroundImage)

  return (
    <motion.div
      className={`relative bg-[#1C1C32] rounded-2xl p-4 md:p-5 border-2 border-[#2A2A45] overflow-hidden ${
        hasArt ? "text-left min-h-[180px] md:min-h-[195px]" : "text-center"
      }`}
      variants={itemVariants}
      whileHover={{ scale: 1.03, y: -3, boxShadow: "0 4px 20px rgba(243, 112, 32, 0.2)" }}
      whileTap={{ scale: 0.98 }}
    >
      {backgroundImage && (
        <>
          <div className="absolute inset-0">
            <img
              src={backgroundImage}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover scale-[1.04]"
              style={{ objectPosition: backgroundPosition }}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C18]/8 via-[#0B0C18]/18 to-[#0B0C18]/36" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#080A14]/28 via-transparent to-[#080A14]/14" />
          <div className="absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-t from-[#080A12]/90 via-[#080A12]/60 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(255,255,255,0.10),transparent_40%)]" />
        </>
      )}

      {/* Background glow */}
      <div
        className="absolute right-0 top-0 w-24 h-24 rounded-full opacity-10 blur-2xl -translate-y-1/2 translate-x-1/2"
        style={{ backgroundColor: color }}
      />

      {/* Plus button */}
      {onPlusClick && (
        <motion.button
          onClick={(e) => { e.stopPropagation(); onPlusClick() }}
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center border-2 border-[#2A2A45] bg-[#1C1C32]/90 backdrop-blur-sm cursor-pointer z-[2]"
          whileHover={{ scale: 1.15, boxShadow: "0 2px 10px rgba(243, 112, 32, 0.1)" }}
          whileTap={{ scale: 0.9,  }}
        >
          <Plus className="w-4 h-4" style={{ color }} />
        </motion.button>
      )}

      <motion.div
        className={`relative z-[1] flex items-center justify-center shadow-lg ${
          hasArt
            ? "absolute left-3 top-3 w-8 h-8 md:w-9 md:h-9 rounded-lg border border-white/15 bg-black/25 backdrop-blur-sm"
            : "w-11 h-11 md:w-12 md:h-12 rounded-xl mx-auto border-2 border-white/20 mb-3"
        }`}
        style={hasArt ? { boxShadow: `0 0 0 1px ${color}20 inset` } : { backgroundColor: color }}
        animate={{ rotate: hasArt ? [0, 2, -2, 0] : [0, 5, -5, 0] }}
        transition={{ delay, duration: 3, repeat: Infinity, ease: "easeInOut" }}
      >
        <Icon
          className={hasArt ? "w-4 h-4 md:w-[18px] md:h-[18px] text-white/75" : "w-5 h-5 md:w-6 md:h-6 text-white"}
          style={hasArt ? { color } : undefined}
        />
      </motion.div>

      <div className={`relative z-[1] ${hasArt ? "pt-14 md:pt-16 flex h-full flex-col justify-end" : ""}`}>
        <p className={`text-xs font-medium mb-0.5 drop-shadow-sm ${hasArt ? "text-white/85" : "text-white/80"}`}>{label}</p>
        <motion.p
          className={`font-black text-white leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)] ${
            hasArt ? "text-[30px] md:text-[34px]" : "text-2xl md:text-3xl"
          }`}
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          transition={{ delay: delay + 0.2, type: "spring", bounce: 0.5 }}
        >
          {value}
        </motion.p>
        {subValue && (
          <p className={`text-[11px] mt-0.5 drop-shadow-sm ${hasArt ? "text-white/70" : "text-white/60"}`}>{subValue}</p>
        )}
      </div>
    </motion.div>
  )
}

function LeaderboardRow({
  rank,
  name,
  deals,
  earnings,
  isCurrentUser,
  index,
}: {
  rank: number
  name: string
  deals: number
  earnings: number
  isCurrentUser?: boolean
  index: number
}) {
  const getRankIcon = () => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />
    if (rank === 2) return <Medal className="w-5 h-5 text-[#6A6A8A]" />
    if (rank === 3) return <Award className="w-5 h-5 text-amber-600" />
    return <span className="text-[#9A9ABF] font-bold text-sm">#{rank}</span>
  }

  const getRankStyle = () => {
    if (isCurrentUser) return "bg-gradient-to-r from-[#F37020]/20 to-[#F37020]/10 border-[#F37020] ring-2 ring-[#F37020]/30"
    if (rank === 1) return "bg-gradient-to-r from-[#FFD700]/20 to-[#FFD700]/10 border-[#FFD700]"
    if (rank === 2) return "bg-gradient-to-r from-[#9A9ABF]/20 to-[#9A9ABF]/10 border-[#3A3A55]"
    if (rank === 3) return "bg-gradient-to-r from-[#CD7F32]/20 to-[#CD7F32]/10 border-[#CD7F32]"
    return "bg-[#1C1C32] border-[#2A2A45]"
  }

  return (
    <motion.div
      className={`flex items-center gap-4 p-3 rounded-xl border-2 ${getRankStyle()} transition-all`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3 + index * 0.05, type: "spring" }}
      whileHover={{ scale: 1.02, x: 8 }}
    >
      <div className="w-8 h-8 flex items-center justify-center">
        {getRankIcon()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-bold text-white truncate">{name}</p>
          {isCurrentUser && (
            <span className="text-[10px] bg-[#F37020] text-white px-2 py-0.5 rounded-full font-bold">
              YOU
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-[#9A9ABF]">
          <span>{deals} deals this month</span>
        </div>
      </div>

      <div className="text-right">
        <p className="font-black text-[#32CD32]">{earnings.toLocaleString()} pts</p>
      </div>
    </motion.div>
  )
}

function PendingBalanceBanner({ amount, deadline }: { amount: number; deadline: string | null }) {
  const deadlineDate = deadline ? new Date(deadline) : null
  const hoursRemaining = deadlineDate
    ? Math.max(0, Math.floor((deadlineDate.getTime() - Date.now()) / (1000 * 60 * 60)))
    : null

  return (
    <motion.div
      className="bg-[#1C1C32] border-2 border-[#2A2A45] rounded-2xl p-5 border border-[#2A2A45]"
      variants={itemVariants}
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <motion.div
            className="w-14 h-14 rounded-xl bg-[#22c55e] flex items-center justify-center border-2 border-[#2A2A45]"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <DollarSign className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <p className="text-sm text-[#E8E4DE] font-bold">Pending Balance</p>
            <motion.p
              className="text-3xl font-black text-[#16a34a]"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              ${amount}
            </motion.p>
            <p className="text-sm text-[#9A9ABF] font-medium mt-1">Submit a deal to claim!</p>
          </div>
        </div>

        {hoursRemaining !== null && (
          <motion.div
            className="flex items-center gap-2 px-4 py-2 bg-amber-400 border-2 border-[#2A2A45] rounded-full border border-[#2A2A45]"
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <AlertCircle className="w-4 h-4 text-black" />
            <span className="text-sm font-black text-black">
              {hoursRemaining > 0 ? `${hoursRemaining}h remaining` : "Expiring soon!"}
            </span>
          </motion.div>
        )}
      </div>
    </motion.div>
  )
}

interface PendingAirdrop {
  _id: string
  currency: "cash" | "points"
  amount: number
  note: string
  claimDeadline: string | null
  createdAt: string
}

function PendingAirdropBanner({
  airdrops,
  onClaim,
  claiming,
}: {
  airdrops: PendingAirdrop[]
  onClaim: (id: string) => void
  claiming: string | null
}) {
  const cashAirdrops = airdrops.filter((a) => a.currency === "cash")
  const pointsAirdrops = airdrops.filter((a) => a.currency === "points")
  const totalCash = cashAirdrops.reduce((sum, a) => sum + a.amount, 0)
  const totalPoints = pointsAirdrops.reduce((sum, a) => sum + a.amount, 0)

  const getTimeRemaining = (deadline: string | null) => {
    if (!deadline) return null
    const ms = new Date(deadline).getTime() - Date.now()
    if (ms <= 0) return 0
    const hours = Math.floor(ms / 3600000)
    if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`
    return `${hours}h`
  }

  const getEarliestDeadline = (list: PendingAirdrop[]) => {
    const deadlines = list
      .map((a) => a.claimDeadline)
      .filter(Boolean)
      .map((d) => new Date(d!).getTime())
    if (deadlines.length === 0) return null
    return new Date(Math.min(...deadlines)).toISOString()
  }

  const renderBanner = (
    type: "cash" | "points",
    totalAmount: number,
    items: PendingAirdrop[]
  ) => {
    const isCash = type === "cash"
    const earliestDeadline = getEarliestDeadline(items)
    const timeLeft = getTimeRemaining(earliestDeadline)
    const isUrgent = typeof timeLeft === "number" || (typeof timeLeft === "string" && timeLeft.startsWith("0"))

    return (
      <motion.div
        key={type}
        className="bg-[#1C1C32] border-2 border-[#2A2A45] rounded-2xl p-5"
        variants={itemVariants}
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <motion.div
              className={`w-14 h-14 rounded-xl flex items-center justify-center border-2 border-[#2A2A45] ${
                isCash ? "bg-[#22c55e]" : "bg-[#F59E0B]"
              }`}
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              {isCash ? (
                <DollarSign className="w-7 h-7 text-white" />
              ) : (
                <Star className="w-7 h-7 text-white" />
              )}
            </motion.div>
            <div>
              <p className="text-sm text-[#E8E4DE] font-bold">
                {isCash ? "Pending Cash Airdrop" : "Pending Points Airdrop"}
              </p>
              <motion.p
                className={`text-3xl font-black ${isCash ? "text-[#16a34a]" : "text-[#F59E0B]"}`}
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {isCash ? `$${totalAmount.toLocaleString()}` : `${totalAmount.toLocaleString()} pts`}
              </motion.p>
              {items[0]?.note && (
                <p className="text-sm text-[#9A9ABF] font-medium mt-1">{items[0].note}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {timeLeft !== null && (
              <motion.div
                className="flex items-center gap-2 px-4 py-2 bg-amber-400 border-2 border-[#2A2A45] rounded-full"
                animate={isUrgent ? { scale: [1, 1.04, 1] } : { scale: [1, 1.02, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <AlertCircle className="w-4 h-4 text-black" />
                <span className="text-sm font-black text-black">
                  {timeLeft === 0 ? "Expiring soon!" : `${timeLeft} left`}
                </span>
              </motion.div>
            )}

            <motion.button
              onClick={() => {
                // Claim all airdrops of this type in sequence
                items.forEach((a) => onClaim(a._id))
              }}
              disabled={claiming !== null}
              className={`px-5 py-2.5 rounded-full font-black text-sm border-2 border-[#2A2A45] transition-all ${
                claiming !== null
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:scale-105 active:scale-95"
              } ${isCash ? "bg-[#22c55e] text-white" : "bg-[#F59E0B] text-black"}`}
              whileTap={{ scale: 0.95 }}
            >
              {claiming !== null ? "Claiming..." : "Claim!"}
            </motion.button>
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <>
      {totalCash > 0 && renderBanner("cash", totalCash, cashAirdrops)}
      {totalPoints > 0 && renderBanner("points", totalPoints, pointsAirdrops)}
    </>
  )
}

function PendingSpinsCard({ count, onOpen }: { count: number; onOpen: () => void }) {
  const hasSpins = count > 0

  return (
    <motion.div
      className="bg-[#1C1C32] border-2 border-[#2A2A45] rounded-2xl p-5"
      variants={itemVariants}
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <motion.div
            className={`w-14 h-14 rounded-xl flex items-center justify-center border-2 border-[#2A2A45] ${
              hasSpins ? "bg-[#9B59B6]" : "bg-[#2A2A45]"
            }`}
            animate={hasSpins ? { rotate: [0, 8, -8, 0] } : undefined}
            transition={hasSpins ? { duration: 2.2, repeat: Infinity } : undefined}
          >
            <RefreshCcw className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <p className="text-sm text-[#E8E4DE] font-bold">Pending Free Spins</p>
            <p className={`text-3xl font-black ${hasSpins ? "text-[#C084FC]" : "text-[#9A9ABF]"}`}>
              {count}
            </p>
            <p className="text-sm text-[#9A9ABF] font-medium mt-1">
              {hasSpins
                ? "Earned from events, streaks, weekly rewards, and bonus wins"
                : "No free spins waiting right now"}
            </p>
          </div>
        </div>

        <motion.button
          type="button"
          onClick={onOpen}
          disabled={!hasSpins}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-[#2A2A45] font-black ${
            hasSpins
              ? "bg-[#9B59B6] text-white cursor-pointer"
              : "bg-[#222240] text-[#6A6A8A] cursor-not-allowed"
          }`}
          whileHover={hasSpins ? { scale: 1.03 } : undefined}
          whileTap={hasSpins ? { scale: 0.97 } : undefined}
        >
          <RefreshCcw className="w-4 h-4" />
          <span>{hasSpins ? `Use ${count === 1 ? "Spin" : "Spins"}` : "No Spins Yet"}</span>
        </motion.button>
      </div>
    </motion.div>
  )
}

function ModalAgentSelector({
  agents,
  selectedAgent,
  onSelect,
  isLoading,
}: {
  agents: AgentInfo[]
  selectedAgent: AgentInfo | null
  onSelect: (agent: AgentInfo) => void
  isLoading: boolean
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-3.5 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl">
        <Loader2 className="w-4 h-4 animate-spin text-[#6A6A8A]" />
        <span className="text-[#6A6A8A] text-sm font-medium">Loading agents...</span>
      </div>
    )
  }

  return (
    <div ref={ref} className="relative">
      <motion.button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 bg-[#1C1C32] border-2 border-[#2A2A45] rounded-xl text-left transition-all duration-200 cursor-pointer ${
          isOpen ? "shadow-[2px_2px_0_0_#9B59B6] border-[#9B59B6]" : "border border-[#2A2A45]"
        }`}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center border-2 ${
            selectedAgent ? "bg-[#9B59B6]/10 border-[#9B59B6]" : "bg-[#1A1A30] border-[#2A2A45]"
          }`}>
            <UserCircle className={`w-5 h-5 ${selectedAgent ? "text-[#9B59B6]" : "text-[#6A6A8A]"}`} />
          </div>
          <div className="min-w-0">
            {selectedAgent ? (
              <>
                <p className="text-white text-sm font-bold truncate">{selectedAgent.displayName}</p>
                <p className="text-[#6A6A8A] text-xs">{selectedAgent.totalDeals} deals submitted</p>
              </>
            ) : (
              <p className="text-[#6A6A8A] text-sm font-medium">Select an agent</p>
            )}
          </div>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-5 h-5 text-[#6A6A8A]" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-[#1C1C32] border-2 border-[#2A2A45] rounded-xl border border-[#2A2A45] overflow-hidden"
          >
            <div className="max-h-48 overflow-y-auto">
              {agents.map((agent, i) => (
                <motion.button
                  key={agent._id}
                  type="button"
                  onClick={() => { onSelect(agent); setIsOpen(false) }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${
                    selectedAgent?._id === agent._id ? "bg-[#9B59B6]/10" : "hover:bg-[#1A1A30]"
                  }`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <div className="w-8 h-8 rounded-lg bg-[#9B59B6]/10 border-2 border-[#9B59B6]/30 flex items-center justify-center text-[#9B59B6] text-xs font-bold">
                    {agent.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{agent.displayName}</p>
                    <p className="text-[#6A6A8A] text-xs">{agent.totalDeals} deals</p>
                  </div>
                  {selectedAgent?._id === agent._id && <Check className="w-5 h-5 text-[#9B59B6]" />}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 4000)
    return () => clearTimeout(timer)
  }, [onClose])

  const isSuccess = type === "success"
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.9 }}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
    >
      <div className={`flex items-center gap-3 px-6 py-4 border-2 border-[#2A2A45] rounded-xl border border-[#2A2A45] ${isSuccess ? "bg-[#32CD32]" : "bg-red-500"}`}>
        {isSuccess ? (
          <motion.div animate={{ rotate: [0, 360] }} transition={{ duration: 0.5 }}>
            <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
          </motion.div>
        ) : (
          <AlertCircle className="w-5 h-5 text-white" />
        )}
        <span className="font-bold text-white">{message}</span>
      </div>
    </motion.div>
  )
}


function QuestsSection() {
  const { user } = useAuth()
  
  const ranks = [
    { name: "Bronze", points: 0, prize: "Welcome Badge", lottie: bronzeCoinLottie, frameOffset: 0, speed: 1 },
    { name: "Silver", points: 500, prize: "$25 Bonus", lottie: silverCoinLottie, frameOffset: 0.45, speed: 0.85 },
    { name: "Gold", points: 1000, prize: "$50 Bonus", lottie: goldCoinLottie, frameOffset: 0.75, speed: 1.15 },
    { name: "Platinum", points: 2500, prize: "$100 Bonus", lottie: platinumLottie, frameOffset: 0.3, speed: 0.9 },
    { name: "Diamond", points: 5000, prize: "$250 Bonus + VIP", lottie: diamondLottie, frameOffset: 0.6, speed: 1.1 },
  ]
  
  const currentPoints = user?.points?.current ?? 0
  const maxPoints = ranks[ranks.length - 1]!.points
  const overallProgress = Math.min(currentPoints / maxPoints, 1)
  
  const currentRankIndex = ranks.reduce((acc, rank, index) => 
    currentPoints >= rank.points ? index : acc, 0)
  const currentRank = ranks[currentRankIndex]
  const nextRank = ranks[currentRankIndex + 1]

  return (
    <motion.div
      className="bg-[#1C1C32] border-2 border-[#2A2A45] rounded-3xl border border-[#2A2A45] overflow-hidden"
      variants={itemVariants}
    >
      <div className="p-5 relative overflow-hidden min-h-[124px] md:min-h-[132px]">
        <div className="absolute inset-0">
          <img
            src={battlePassHeaderBg}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-center scale-[1.02]"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#14081F]/18 via-transparent to-[#13070F]/30" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_30%,rgba(255,255,255,0.18),transparent_45%)]" />
        <motion.div
          className="absolute right-2 md:right-6 bottom-0 pointer-events-none hidden sm:block"
          animate={{ y: [0, -4, 0], rotate: [0, -1.5, 0.8, 0] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="absolute inset-0 blur-xl opacity-35 bg-[radial-gradient(circle_at_45%_35%,#FFD700,transparent_62%)]" />
          <img
            src={battlePassPillCutout}
            alt=""
            aria-hidden="true"
            className="relative h-[112px] md:h-[136px] w-auto object-contain drop-shadow-[0_12px_22px_rgba(0,0,0,0.35)]"
          />
        </motion.div>
        <div className="relative flex items-center justify-between flex-wrap gap-4 pr-0 sm:pr-28 md:pr-36">
          <div className="flex items-center gap-3">
            <motion.div animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }} transition={{ duration: 2, repeat: Infinity }}>
              <Swords className="w-8 h-8 text-white drop-shadow-lg" />
            </motion.div>
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-white text-shadow-retro-sm">BATTLE PASS</h2>
              <p className="text-white/80 text-sm">{currentRank?.name} Rank</p>
            </div>
          </div>
          <div className="bg-[#1C1C32]/20 backdrop-blur-sm rounded-full px-4 py-2">
            <span className="text-white font-black text-lg">{currentPoints.toLocaleString()}</span>
            <span className="text-white/80 text-sm ml-1">pts</span>
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="relative mb-6">
          <div className="w-full h-8 bg-[#1A1A30] rounded-full border-2 border-[#2A2A45]/20 overflow-hidden relative">
            <motion.div
              className="h-full rounded-full relative overflow-hidden"
              style={{ background: "linear-gradient(90deg, #F37020, #FFD700, #9B59B6)" }}
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress * 100}%` }}
              transition={{ type: "spring", bounce: 0.2, duration: 1.2 }}
            >
              <motion.div
                className="absolute inset-0"
                style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)" }}
                animate={{ x: ["-100%", "200%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
              />
            </motion.div>
            <motion.div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-[#1C1C32] border-2 border-[#2A2A45] rounded-full  z-10"
              style={{ left: `calc(${overallProgress * 100}% - 8px)` }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.5 }}
            />
          </div>
          <div className="absolute top-0 left-0 right-0 h-8 pointer-events-none">
            {ranks.slice(1).map((rank) => (
              <div key={rank.name} className="absolute top-1/2 -translate-y-1/2 w-1 h-5 bg-black/20" style={{ left: `${(rank.points / maxPoints) * 100}%` }} />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-5 gap-2 md:gap-3">
          {ranks.map((rank, index) => {
            const isCompleted = currentPoints >= rank.points
            const isCurrent = index === currentRankIndex
            const isNext = index === currentRankIndex + 1
            return (
              <motion.div
                key={rank.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-2 md:p-3 rounded-xl border-3 text-center transition-all ${
                  isCurrent ? "bg-gradient-to-br from-[#F37020]/20 to-[#FFD700]/20 border-[#F37020] shadow-[4px_4px_0_0_#F37020]"
                  : isCompleted ? "bg-[#32CD32]/10 border-[#32CD32]"
                  : isNext ? "bg-[#1C1C32] border-[#2A2A45] border border-[#2A2A45]"
                  : "bg-[#1A1A30] border-[#2A2A45]/10"
                }`}
              >
                <div className={`mb-1 flex justify-center ${isCompleted || isCurrent ? "" : "opacity-60"}`}>
                  {isCompleted && !isCurrent ? (
                    <div className="w-8 h-8 mx-auto rounded-full bg-[#32CD32] flex items-center justify-center border-2 border-[#2A2A45]">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-10 h-10 md:w-12 md:h-12">
                      <DotLottieReact
                        src={rank.lottie}
                        loop
                        autoplay
                        speed={rank.speed}
                        style={{ width: "100%", height: "100%" }}
                        dotLottieRefCallback={(dotLottie) => {
                          if (dotLottie) {
                            dotLottie.addEventListener("load", () => {
                              const total = dotLottie.totalFrames || 60
                              dotLottie.setFrame(Math.floor(total * rank.frameOffset))
                            })
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <p className={`text-xs md:text-sm font-bold truncate ${isCurrent ? "text-[#F37020]" : isCompleted ? "text-[#32CD32]" : "text-[#9A9ABF]"}`} style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}>{rank.name}</p>
                <p className="text-[10px] md:text-xs text-[#6A6A8A] font-medium" style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}>{rank.points.toLocaleString()} pts</p>
                <p className="text-[9px] md:text-[10px] text-[#9A9ABF] mt-1 truncate hidden md:block" style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}>{rank.prize}</p>
              </motion.div>
            )
          })}
        </div>
        {nextRank && (
          <motion.div className="mt-5 pt-5 border-t-3 border-dashed border-[#2A2A45]/10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#9B59B6]/10 flex items-center justify-center border-2 border-[#9B59B6]">
                  <Gift className="w-5 h-5 text-[#9B59B6]" />
                </div>
                <div>
                  <p className="text-sm text-[#9A9ABF] font-medium" style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}>Next Reward</p>
                  <p className="font-bold text-[#9B59B6]" style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}>{nextRank.prize}</p>
                </div>
              </div>
              <div className="bg-[#9B59B6]/10 rounded-full px-4 py-2 border-2 border-[#9B59B6]/30">
                <span className="text-sm text-[#9B59B6] font-bold" style={{ fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }}>{(nextRank.points - currentPoints).toLocaleString()} pts away</span>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  )
}

export function DashboardTab() {
  const { user, getLeaderboard, getDeals, submitDeal, getAgencyAgents, getUserStreaks, claimStreak, getStreakHistory, markStreakSeen, getUserPendingAirdrops, claimAirdrop } = useAuth()
  const [leaderboardData, setLeaderboardData] = React.useState<LeaderboardData | null>(null)
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = React.useState(true)
  const [leaderboardTab, setLeaderboardTab] = React.useState<"agency" | "agent">("agency")

  // Deal state
  const [deals, setDeals] = React.useState<Deal[]>([])
  const [isLoadingDeals, setIsLoadingDeals] = React.useState(true)

  // Modal state
  const [showDealModal, setShowDealModal] = React.useState(false)
  const [showFreeSpinModal, setShowFreeSpinModal] = React.useState(false)
  const [memberId, setMemberId] = React.useState("")
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [selectedAgent, setSelectedAgent] = React.useState<AgentInfo | null>(null)
  const [agents, setAgents] = React.useState<AgentInfo[]>([])
  const [agentsLoading, setAgentsLoading] = React.useState(true)
  const [toast, setToast] = React.useState<{ message: string; type: "success" | "error" } | null>(null)
  const [inputFocused, setInputFocused] = React.useState(false)
  const [rewardSlotDeal, setRewardSlotDeal] = React.useState<{
    dealId: string
    memberId: string
    recipientLabel?: string
  } | null>(null)
  const [isRewardSlotBusy, setIsRewardSlotBusy] = React.useState(false)
  const [isFreeSpinSlotBusy, setIsFreeSpinSlotBusy] = React.useState(false)

  // Streak popup state
  const [showStreakPopup, setShowStreakPopup] = React.useState(false)
  const [streakData, setStreakData] = React.useState<StreakData | null>(null)
  const [streakChecked, setStreakChecked] = React.useState(false)

  // Pending airdrops (ones with claim deadlines)
  const [pendingAirdrops, setPendingAirdrops] = React.useState<PendingAirdrop[]>([])
  const [claimingAirdropId, setClaimingAirdropId] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    getUserPendingAirdrops().then((res) => {
      if (cancelled) return
      console.log("[Airdrops] fetch result:", res)
      if (res.success && res.airdrops && res.airdrops.length > 0) {
        setPendingAirdrops(res.airdrops)
      }
    }).catch((err) => {
      console.error("[Airdrops] fetch error:", err)
    })
    return () => { cancelled = true }
  }, [getUserPendingAirdrops])

  const handleClaimAirdrop = React.useCallback(async (airdropId: string) => {
    setClaimingAirdropId(airdropId)
    const result = await claimAirdrop(airdropId)
    if (result.success) {
      setPendingAirdrops((prev) => prev.filter((a) => a._id !== airdropId))
    }
    setClaimingAirdropId(null)
  }, [claimAirdrop])

  const displayName =
    user?.onboardingData?.displayName ||
    user?.onboardingData?.agencyName ||
    user?.email?.split("@")[0] ||
    "Champion"

  const pendingBalance = user?.balance?.pending || 0
  const pendingFreeSpins = user?.freeSpins || 0
  const welcomeRewardDeadline = user?.welcomeReward?.claimDeadline || null
  const userType = user?.onboardingData?.userType
  const isAgency = userType === "agency"
  const hasNoAgents = isAgency && !agentsLoading && agents.length === 0
  const isShowingRewardSlot = rewardSlotDeal !== null

  // Bonus stats should come from real balances, not point conversion heuristics
  const availableBonus = user?.balance?.available || 0
  const totalBonusTracked = availableBonus + pendingBalance
  const totalDeals = user?.points?.totalDeals || 0
  const monthlyDeals = user?.points?.monthlyDeals || 0

  // Fetch leaderboard data when tab changes
  React.useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoadingLeaderboard(true)
      const result = await getLeaderboard(leaderboardTab)
      if (result.success && result.data) {
        setLeaderboardData(result.data)
      }
      setIsLoadingLeaderboard(false)
    }
    fetchLeaderboard()
  }, [getLeaderboard, leaderboardTab])


  // Check for unclaimed daily login streak on mount (only show once per day)
  React.useEffect(() => {
    const checkStreak = async () => {
      if (streakChecked) return
      setStreakChecked(true)
      const result = await getUserStreaks()
      if (result.success && result.streaks) {
        const dailyLoginStreak = result.streaks.find(s => s.streakType === "daily_login" && s.canClaimToday && !s.popupShownToday)
        if (dailyLoginStreak) {
          const historyResult = await getStreakHistory(dailyLoginStreak.streakId)
          if (historyResult.success && historyResult.history) {
            setStreakData({
              streakId: dailyLoginStreak.streakId,
              streakName: dailyLoginStreak.streakName,
              currentStreak: dailyLoginStreak.currentStreak,
              canClaimToday: dailyLoginStreak.canClaimToday,
              todayReward: dailyLoginStreak.todayReward,
              nextMilestone: dailyLoginStreak.nextMilestone,
              history: historyResult.history,
            })
            setShowStreakPopup(true)
            // Mark as seen server-side so it won't show again today
            markStreakSeen(dailyLoginStreak.streakId)
          }
        }
      }
    }
    checkStreak()
  }, [getUserStreaks, getStreakHistory, markStreakSeen, streakChecked])

  // Fetch deals on mount
  React.useEffect(() => {
    const fetchDeals = async () => {
      const result = await getDeals()
      if (result.success && result.deals) setDeals(result.deals)
      setIsLoadingDeals(false)
    }
    fetchDeals()
  }, [getDeals])

  // Fetch agency agents if user is agency type
  React.useEffect(() => {
    if (!isAgency) {
      setAgentsLoading(false)
      return
    }
    setAgentsLoading(true)
    getAgencyAgents().then((result) => {
      if (result.success && result.agents) setAgents(result.agents)
      setAgentsLoading(false)
    })
  }, [isAgency, user?.generatedAgencyId])

  const resetDealModal = React.useCallback(() => {
    setMemberId("")
    setSelectedAgent(null)
    setInputFocused(false)
    setRewardSlotDeal(null)
    setIsRewardSlotBusy(false)
  }, [])

  const handleCloseDealModal = React.useCallback(() => {
    if (isSubmitting || isRewardSlotBusy) return
    setShowDealModal(false)
    resetDealModal()
  }, [isSubmitting, isRewardSlotBusy, resetDealModal])

  const handleCloseFreeSpinModal = React.useCallback(() => {
    if (isFreeSpinSlotBusy) return
    setShowFreeSpinModal(false)
    setIsFreeSpinSlotBusy(false)
  }, [isFreeSpinSlotBusy])

  const handleRewardSlotSettled = React.useCallback((reward: DealSlotReward) => {
    const bonusCashTotal = reward.type === "bonus"
      ? (typeof reward.totalCashAmount === "number" ? reward.totalCashAmount : reward.amount)
      : reward.amount
    if (reward.type === "points") {
      setToast({ message: `Slot reward: +${reward.amount} points!`, type: "success" })
      return
    }
    if (reward.type === "bonus") {
      setToast({ message: `Slot reward: $${bonusCashTotal} bonus added!`, type: "success" })
      return
    }
    if (reward.type === "item") {
      setToast({ message: `Slot reward: ${reward.label || "Oval merch"} won!`, type: "success" })
      return
    }
    if (reward.type === "free_spins") {
      setToast({ message: `Slot reward: ${reward.amount} free spin${reward.amount === 1 ? "" : "s"}!`, type: "success" })
      return
    }
    if (reward.type === "side_quest") {
      setToast({ message: "Side Quest unlocked! Check the sidebar for details.", type: "success" })
      return
    }
    setToast({ message: "Deal submitted! No extra slot reward this time.", type: "success" })
  }, [])


  // Handle streak claim
  const handleClaimStreak = async (streakId: string) => {
    const result = await claimStreak(streakId)
    if (result.success && result.result) {
      return { success: true, newStreak: result.result.newStreak, reward: result.result.reward }
    }
    return { success: false, message: result.message }
  }

  const handleSubmitDeal = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!memberId.trim() || isSubmitting) return
    if (isAgency && !selectedAgent) return

    setIsSubmitting(true)
    const submittedMemberId = memberId.trim()
    const recipientLabel = isAgency ? (selectedAgent?.displayName || "your agent") : "you"
    const result = await submitDeal(submittedMemberId, isAgency ? selectedAgent?._id : undefined)
    if (result.success && result.deal) {
      setDeals((prev) => [result.deal!, ...prev])
      setMemberId("")
      setSelectedAgent(null)
      if (result.deal._id) {
        setRewardSlotDeal({
          dealId: result.deal._id,
          memberId: result.deal.memberId || submittedMemberId.toUpperCase(),
          recipientLabel,
        })
      } else {
        setToast({ message: "Deal submitted successfully!", type: "success" })
        setShowDealModal(false)
        resetDealModal()
      }
    } else {
      setToast({ message: result.message || "Failed to submit deal", type: "error" })
    }
    setIsSubmitting(false)
  }

  const isFormValid = memberId.trim() && !isSubmitting && (!isAgency || selectedAgent)

  return (
    <motion.div
      className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Hero Banner */}
      {/* Streak Popup */}
      <StreakPopup
        isOpen={showStreakPopup}
        onClose={() => setShowStreakPopup(false)}
        streakData={streakData}
        onClaim={handleClaimStreak}
      />

      <motion.div
        className="relative rounded-3xl overflow-hidden border-2 border-[#2A2A45] mb-8 group/banner"
        variants={itemVariants}
        whileHover={{ y: -2, boxShadow: "0 8px 30px rgba(243, 112, 32, 0.25)" }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <img
          src={bannerImg}
          alt=""
          className="w-full h-52 sm:h-60 md:h-68 lg:h-72 object-cover transition-transform duration-700 group-hover/banner:scale-105"
        />

        {/* Gradient overlay — heavier on left for text, transparent on right for mascot */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#121220]/70 via-[#121220]/30 to-transparent" />

        {/* Animated shimmer sweep */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-[-20deg]"
          initial={{ x: "-150%" }}
          animate={{ x: "250%" }}
          transition={{ duration: 4, repeat: Infinity, repeatDelay: 3, ease: "easeInOut" }}
        />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-10 lg:px-14">
          <motion.p
            className="font-display text-[#FFD700] text-sm md:text-base tracking-widest uppercase mb-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            style={{ textShadow: "1px 1px 0 #121220" }}
          >
            Welcome back, {displayName}
          </motion.p>

          <motion.h1
            className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[0.95] max-w-sm md:max-w-md"
            style={{
              textShadow: "4px 4px 0 #121220, -1px -1px 0 #121220, 1px -1px 0 #121220, -1px 1px 0 #121220",
            }}
          >
            Ready to make{" "}
            <motion.span
              className="text-[#FFD700] inline-block"
              animate={{ scale: [1, 1.05, 1], rotate: [-1, 1, -1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              some money?
            </motion.span>
          </motion.h1>

          <motion.button
            onClick={() => setShowDealModal(true)}
            className="mt-5 w-fit group/cta relative flex items-center gap-2.5 pl-5 pr-4 py-2.5 bg-[#1C1C32]/95 backdrop-blur-sm rounded-full border border-white/60 shadow-lg cursor-pointer transition-all hover:bg-[#1C1C32] hover:shadow-xl overflow-hidden"
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent translate-x-[-150%] group-hover/cta:translate-x-[150%] transition-transform duration-700 skew-x-[-20deg]" />
            <span className="relative font-body font-medium text-sm md:text-base text-[#F37020]">
              Submit a Deal
            </span>
            <motion.span
              className="relative inline-flex items-center justify-center w-6 h-6 bg-[#F37020]/10 rounded-full text-[#F37020] text-xs"
              animate={{ x: [0, 2, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              →
            </motion.span>
          </motion.button>
        </div>
      </motion.div>

      {/* Pending Balance Banner */}
      {pendingBalance > 0 && (
        <div className="mb-6">
          <PendingBalanceBanner amount={pendingBalance} deadline={welcomeRewardDeadline} />
        </div>
      )}

      {/* Pending Airdrop Banners (cash & points with claim deadlines) */}
      {pendingAirdrops.length > 0 && (
        <div className="mb-6 space-y-3">
          <PendingAirdropBanner
            airdrops={pendingAirdrops}
            onClaim={handleClaimAirdrop}
            claiming={claimingAirdropId}
          />
        </div>
      )}

      {pendingFreeSpins > 0 && (
        <div className="mb-6">
          <PendingSpinsCard count={pendingFreeSpins} onOpen={() => setShowFreeSpinModal(true)} />
        </div>
      )}

      {/* Stats Grid */}
      <motion.div
        className="grid grid-cols-3 gap-3 md:gap-4 mb-8"
        variants={containerVariants}
      >
        <StatCard
          icon={DollarSign}
          label="Earnings"
          value={`$${totalBonusTracked.toLocaleString()}`}
          subValue={`$${availableBonus.toLocaleString()} available • $${pendingBalance.toLocaleString()} pending`}
          color="#32CD32"
          backgroundImage={earningsCardBg}
          backgroundPosition="58% center"
          delay={0}
          onPlusClick={() => setShowDealModal(true)}
        />
        <StatCard
          icon={Handshake}
          label="Deals"
          value={totalDeals.toString()}
          subValue="Completed"
          color="#F37020"
          backgroundImage={dealsCardBg}
          backgroundPosition="56% center"
          delay={0.1}
          onPlusClick={() => setShowDealModal(true)}
        />
        <StatCard
          icon={TrendingUp}
          label="Bonus"
          value={`${monthlyDeals}`}
          subValue={`Tracked total: $${totalBonusTracked.toLocaleString()}`}
          color="#9B59B6"
          backgroundImage={bonusCardBg}
          backgroundPosition="58% center"
          delay={0.2}
          onPlusClick={() => setShowDealModal(true)}
        />
      </motion.div>

      {/* Battle Pass Section */}
      <QuestsSection />

      {/* Leaderboard Section */}
      <motion.div
        className="bg-[#1C1C32] border-2 border-[#2A2A45] rounded-3xl border border-[#2A2A45] overflow-hidden"
        variants={itemVariants}
      >
        {/* Leaderboard Header */}
        <div className="p-5 relative overflow-hidden min-h-[124px] md:min-h-[132px]">
          <div className="absolute inset-0">
            <img
              src={leaderboardHeaderBg}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover object-center scale-[1.02]"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A0E12]/25 via-transparent to-[#160B10]/30" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_25%,rgba(255,255,255,0.12),transparent_42%)]" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Trophy className="w-8 h-8 text-white drop-shadow-lg" />
              </motion.div>
              <div>
                <h2 className="font-display text-2xl md:text-3xl text-white text-shadow-retro-sm">
                  TOP 10 LEADERBOARD
                </h2>
                <p className="text-white/80 text-sm">This month's top performers</p>
              </div>
            </div>
            {/* Tab Switcher */}
            <div className="flex items-center bg-[#1C1C32]/20 rounded-full p-1 backdrop-blur-sm">
              <button
                onClick={() => setLeaderboardTab("agency")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm transition-all ${
                  leaderboardTab === "agency"
                    ? "bg-[#1C1C32] text-[#F37020] shadow-md"
                    : "text-white hover:bg-[#1C1C32]/10"
                }`}
              >
                <Building2 className="w-4 h-4" />
                <span>Agencies</span>
              </button>
              <button
                onClick={() => setLeaderboardTab("agent")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm transition-all ${
                  leaderboardTab === "agent"
                    ? "bg-[#1C1C32] text-[#F37020] shadow-md"
                    : "text-white hover:bg-[#1C1C32]/10"
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Agents</span>
              </button>
            </div>
          </div>
        </div>

        {/* Leaderboard Content */}
        <div className="p-5">
          {isLoadingLeaderboard ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#6A6A8A]" />
            </div>
          ) : leaderboardData && (leaderboardData.leaderboard.length > 0 || leaderboardData.currentUserPosition) ? (
            <div className="space-y-3">
              {leaderboardData.leaderboard.map((entry, index) => (
                <LeaderboardRow
                  key={`${entry.rank}-${entry.name}`}
                  rank={entry.rank}
                  name={entry.name}
                  deals={entry.deals}
                  earnings={entry.earnings}
                  isCurrentUser={entry.isCurrentUser}
                  index={index}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <motion.div
                className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#1A1A30] flex items-center justify-center"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                {leaderboardTab === "agency" ? (
                  <Building2 className="w-8 h-8 text-[#6A6A8A]" />
                ) : (
                  <Users className="w-8 h-8 text-[#6A6A8A]" />
                )}
              </motion.div>
              <p className="text-[#9A9ABF] font-medium">
                {leaderboardTab === "agency"
                  ? "No agency rankings yet this month"
                  : "No team rankings yet this month"}
              </p>
              <p className="text-[#6A6A8A] text-sm mt-1">
                {leaderboardTab === "agency"
                  ? "Agencies will appear here once they submit deals!"
                  : "Your team members will appear here once they submit deals!"}
              </p>
            </div>
          )}

          {/* Your Position (if not in top 10) */}
          {leaderboardData?.currentUserPosition && !leaderboardData.leaderboard.some(e => e.isCurrentUser) && (
            <motion.div
              className="mt-6 p-4 bg-gradient-to-r from-[#F37020]/10 to-[#F37020]/5 border-2 border-[#F37020] rounded-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <span className="text-[#F37020] font-bold">#{leaderboardData.currentUserPosition.rank}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white">{leaderboardData.currentUserPosition.name}</span>
                      <span className="text-[10px] bg-[#F37020] text-white px-2 py-0.5 rounded-full font-bold">
                        YOU
                      </span>
                    </div>
                    <span className="text-xs text-[#9A9ABF]">{leaderboardData.currentUserPosition.deals} deals this month</span>
                  </div>
                </div>
                <p className="font-black text-[#32CD32]">{leaderboardData.currentUserPosition.earnings.toLocaleString()} pts</p>
              </div>
            </motion.div>
          )}

          {/* Not ranked yet message */}
          {!leaderboardData?.currentUserPosition && !leaderboardData?.leaderboard.some(e => e.isCurrentUser) && totalDeals === 0 && (
            <motion.div
              className="mt-6 p-4 bg-gradient-to-r from-[#F37020]/30 to-[#F37020]/10 border-2 border-dashed border-[#F37020] rounded-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Star className="w-5 h-5 text-[#F37020]" />
                  <span className="font-bold text-[#E8E4DE]">Your Position</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#9A9ABF]">Not ranked yet</span>
                  <span className="text-xs bg-[#F37020] text-white px-2 py-1 rounded-full font-bold">
                    Submit a deal to rank!
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Recent Deals Section */}
      <motion.div
        className="bg-[#1C1C32] border-2 border-[#2A2A45] rounded-3xl border border-[#2A2A45] overflow-hidden mt-8"
        variants={itemVariants}
      >
        {/* Recent Deals Header */}
        <div className="p-5 relative overflow-hidden min-h-[124px] md:min-h-[132px]">
          <div className="absolute inset-0">
            <img
              src={recentDealsHeaderBg}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover object-center scale-[1.02]"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#07140E]/36 via-[#08150F]/10 to-[#06120C]/24" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_24%,rgba(255,255,255,0.10),transparent_40%)]" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <Handshake className="w-7 h-7 text-white drop-shadow-lg" />
              </motion.div>
              <div>
                <h2 className="font-display text-2xl md:text-3xl text-white text-shadow-retro-sm">
                  RECENT DEALS
                </h2>
                <p className="text-white/80 text-sm">Your latest submissions</p>
              </div>
            </div>
            <motion.button
              onClick={() => setShowDealModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#1C1C32] rounded-full border-2 border-[#2A2A45] font-bold text-sm text-[#32CD32] cursor-pointer"
              whileHover={{ scale: 1.05, boxShadow: "0 4px 15px rgba(243, 112, 32, 0.15)" }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus className="w-4 h-4" />
              <span>New Deal</span>
            </motion.button>
          </div>
        </div>

        {/* Recent Deals Content */}
        <div className="p-5">
          {isLoadingDeals ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#6A6A8A]" />
            </div>
          ) : deals.length > 0 ? (
            <div className="space-y-3">
              {deals.slice(0, 5).map((deal, index) => {
                const date = new Date(deal.createdAt)
                return (
                  <motion.div
                    key={deal._id}
                    className="flex items-center gap-4 p-3 rounded-xl border-2 border-[#2A2A45] bg-[#1C1C32] transition-all"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.05, type: "spring" }}
                    whileHover={{ scale: 1.02, x: 8 }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#32CD32]/10 border-2 border-[#32CD32]/30 flex items-center justify-center">
                      <Handshake className="w-5 h-5 text-[#32CD32]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-white truncate">{deal.memberId}</p>
                      <div className="flex items-center gap-1 text-xs text-[#6A6A8A]">
                        <Calendar className="w-3 h-3" />
                        <span>{date.toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 px-2.5 py-1 bg-[#32CD32]/15 border-2 border-[#32CD32] rounded-lg">
                      <Zap className="w-3.5 h-3.5 text-[#32CD32]" />
                      <span className="text-sm font-bold text-[#32CD32]">+{deal.pointsEarned} pts</span>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <motion.div
                className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#32CD32]/10 flex items-center justify-center"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <Handshake className="w-8 h-8 text-[#32CD32]/40" />
              </motion.div>
              <p className="text-[#9A9ABF] font-medium">No deals yet</p>
              <p className="text-[#6A6A8A] text-sm mt-1">Submit your first deal to start earning points!</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Deal Submit Modal */}
      <AnimatePresence>
        {showDealModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseDealModal}
            />

            {/* Modal Card */}
            <motion.div
              className={`relative bg-[#1C1C32] border-2 border-[#2A2A45] rounded-2xl w-full overflow-hidden ${
                isShowingRewardSlot ? "max-w-xl max-h-[92vh] flex flex-col" : "max-w-md"
              }`}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", bounce: 0.3 }}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-[#32CD32] via-[#2DBE2D] to-[#28A828] p-5 relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 opacity-20 pointer-events-none"
                  style={{
                    backgroundImage: "linear-gradient(45deg, transparent, transparent)",
                  }}
                  animate={{ x: [0, 40] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
                <motion.button
                  type="button"
                  onClick={handleCloseDealModal}
                  className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/20 flex items-center justify-center text-white hover:bg-black/40 transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </motion.button>
                <div className="relative pr-12 flex items-center gap-3">
                  <motion.div
                    className="w-12 h-12 rounded-xl bg-[#1C1C32]/20 flex items-center justify-center"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Plus className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-black text-white text-shadow-retro-sm">
                      {isShowingRewardSlot ? "Spin Deal Reward" : "Submit New Deal"}
                    </h3>
                    <p className="text-white/80 text-sm font-medium">
                      {isShowingRewardSlot
                        ? "Same modal, instant bonus spin after your deal is recorded"
                        : isAgency ? "Submit on behalf of your agents" : "Earn 10 points per deal"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className={isShowingRewardSlot ? "p-6 min-h-0 overflow-y-auto" : "p-6"}>
                <AnimatePresence mode="wait">
                  {isShowingRewardSlot && rewardSlotDeal ? (
                    <motion.div
                      key="reward-slot"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <DealRewardSlotStep
                        key={rewardSlotDeal.dealId}
                        dealId={rewardSlotDeal.dealId}
                        memberId={rewardSlotDeal.memberId}
                        recipientLabel={rewardSlotDeal.recipientLabel}
                        onBusyChange={setIsRewardSlotBusy}
                        onRewardSettled={handleRewardSlotSettled}
                        onSubmitAnother={() => {
                          setRewardSlotDeal(null)
                          setIsRewardSlotBusy(false)
                        }}
                        onClose={handleCloseDealModal}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="deal-form"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      {/* No agents warning */}
                      {hasNoAgents && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border-3 border-[#CD7F32] shadow-[3px_3px_0_0_#d97706] mb-5"
                        >
                          <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-amber-800 text-sm font-bold">No agents available</p>
                            <p className="text-amber-600 text-xs mt-1">
                              You need agents in your agency to submit deals. Share your Agency ID so agents can join.
                            </p>
                          </div>
                        </motion.div>
                      )}

                      <form onSubmit={handleSubmitDeal} className="space-y-4">
                        {/* Agent selector for agency users */}
                        {isAgency && !hasNoAgents && (
                          <div className="space-y-2">
                            <label className="block text-sm font-bold text-[#E8E4DE] flex items-center gap-1.5">
                              <Users className="w-4 h-4 text-[#9B59B6]" />
                              Select Agent
                            </label>
                            <ModalAgentSelector
                              agents={agents}
                              selectedAgent={selectedAgent}
                              onSelect={setSelectedAgent}
                              isLoading={agentsLoading}
                            />
                          </div>
                        )}

                        {/* Member ID Input */}
                        <div className="space-y-2">
                          <label htmlFor="modalMemberId" className="block text-sm font-bold text-[#E8E4DE]">Member ID</label>
                          <motion.div
                            className="relative"
                            animate={{ scale: inputFocused ? 1.01 : 1 }}
                            transition={{ type: "spring", stiffness: 400, damping: 30 }}
                          >
                            <input
                              type="text"
                              id="modalMemberId"
                              value={memberId}
                              onChange={(e) => setMemberId(e.target.value)}
                              onFocus={() => setInputFocused(true)}
                              onBlur={() => setInputFocused(false)}
                              placeholder="Enter member ID..."
                              disabled={isSubmitting || hasNoAgents}
                              className={`w-full px-4 py-3.5 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium text-white placeholder:text-[#6A6A8A] focus:outline-none transition-all duration-200 disabled:opacity-40 ${
                                inputFocused ? "shadow-[3px_3px_0_0_#32CD32] border-[#32CD32] bg-[#1C1C32]" : "border border-[#2A2A45]"
                              }`}
                            />
                          </motion.div>
                        </div>

                        {/* Submit Button */}
                        <motion.button
                          type="submit"
                          disabled={!isFormValid || hasNoAgents}
                          className={`w-full flex items-center justify-center gap-2.5 px-5 py-4 border-2 border-[#2A2A45] rounded-xl font-bold text-lg transition-all cursor-pointer ${
                            isFormValid
                              ? "bg-[#32CD32] text-white border border-[#2A2A45]"
                              : "bg-[#222240] text-[#6A6A8A] cursor-not-allowed border border-[#2A2A45]"
                          }`}
                          whileHover={isFormValid ? { scale: 1.02, boxShadow: "0 4px 20px rgba(243, 112, 32, 0.2)" } : {}}
                          whileTap={isFormValid ? { scale: 0.98, boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)", x: 2, y: 2 } : {}}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Handshake className="w-5 h-5" />
                              {isAgency
                                ? selectedAgent ? `Submit for ${selectedAgent.displayName}` : "Select an agent first"
                                : "Submit Deal"
                              }
                            </>
                          )}
                        </motion.button>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFreeSpinModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseFreeSpinModal}
            />

            <motion.div
              className="relative bg-[#1C1C32] border-2 border-[#2A2A45] rounded-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", bounce: 0.3 }}
            >
              <div className="bg-gradient-to-r from-[#9B59B6] via-[#8E44AD] to-[#7D3C98] p-5 relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 opacity-20 pointer-events-none"
                  style={{ backgroundImage: "linear-gradient(45deg, transparent, transparent)" }}
                  animate={{ x: [0, 40] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
                <motion.button
                  type="button"
                  onClick={handleCloseFreeSpinModal}
                  className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/20 flex items-center justify-center text-white hover:bg-black/40 transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </motion.button>
                <div className="relative pr-12 flex items-center gap-3">
                  <motion.div
                    className="w-12 h-12 rounded-xl bg-[#1C1C32]/20 flex items-center justify-center"
                    animate={{ rotate: [0, 8, -8, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <RefreshCcw className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-black text-white text-shadow-retro-sm">
                      Use Your Free Spins
                    </h3>
                    <p className="text-white/80 text-sm font-medium">
                      Spin now for points, bonus cash, merch, side quests, or even more free spins
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 min-h-0 overflow-y-auto">
                <DealRewardSlotStep
                  freeSpinOnly
                  initialFreeSpins={pendingFreeSpins}
                  memberId="FREE SPIN"
                  recipientLabel={isAgency ? "your agency" : "you"}
                  onBusyChange={setIsFreeSpinSlotBusy}
                  onRewardSettled={handleRewardSlotSettled}
                  onSubmitAnother={handleCloseFreeSpinModal}
                  onClose={handleCloseFreeSpinModal}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>

      {/* Floating decoration */}
      <motion.div
        className="fixed bottom-8 right-8 pointer-events-none hidden lg:block"
        animate={{ y: [0, -15, 0], rotate: [-5, 5, -5] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src={moneyImg} alt="" className="w-24 h-auto opacity-50" />
      </motion.div>
    </motion.div>
  )
}
